import React, { useEffect, useRef, useState } from 'react';
import { useMediaDevices } from '@/contexts/MediaDevicesContext';
import { usePeer } from '@/contexts/WebRTCContext.tsx';
import { DefaultLayout } from './DefaultLayout';
import { PiPLayout } from './PiPLayout';

interface VideoFeedsProps {
  currentLayout: 'default' | 'picture-in-picture';
  swapPositions?: boolean;
  videoSizeRatio: number;
  triggerResetConnection: boolean;
  setTriggerResetConnection: React.Dispatch<React.SetStateAction<boolean>>;
  sessionActive: boolean;
  handleSessionActive: (sessionActive: boolean) => void;
  selfFilter: string;
}

export const VideoFeeds = React.memo(function VideoFeeds({
  currentLayout,
  swapPositions,
  videoSizeRatio,
  triggerResetConnection,
  setTriggerResetConnection,
  sessionActive,
  handleSessionActive,
  selfFilter
}: VideoFeedsProps) {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Peer operations
  const [ isPeerVideoTrackMuted, setIsPeerVideoTrackMuted ] = useState({ trackMuted: true });
  const [ peerVideoStatus, setPeerVideoStatus ] = useState<'waiting' | 'connecting' | 'reconnecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [ resetVideoFeeds, setResetVideoFeeds ] = useState(false);

  // Media devices
  const { localStream, isVideoEnabled } = useMediaDevices();

  // Peer context
  const {
    remoteStream,
    peerAudioEnabled,
    peerVideoEnabled,
    peerFilter,
    rtcConnectionState,
    isPeerAudioMuted,
    isPeerVideoHidden,
    peerCountry,
    userCountry
  } = usePeer();

  useEffect(() => {
    // acting locally to global event trigger
    if (triggerResetConnection) {
      setResetVideoFeeds(true);
    }
  }, [ triggerResetConnection ]);

  useEffect(() => {
    if (!resetVideoFeeds) return;
    // resetting logic
    console.log('resetting video feeds');
    // resetting logic
    setResetVideoFeeds(false);
  }, [ resetVideoFeeds ]);

  useEffect(() => {
    if (sessionActive) {
      if (!remoteStream) {
        if (peerVideoStatus !== 'waiting') {
          setPeerVideoStatus('waiting');
          setIsPeerVideoTrackMuted({
            trackMuted: true
          });
        }
      } else {
        if (isPeerVideoTrackMuted.trackMuted && peerVideoStatus !== 'connecting') {
          setPeerVideoStatus('connecting');
        } else if (!isPeerVideoTrackMuted.trackMuted && peerVideoStatus !== 'connected') {
          setPeerVideoStatus('connected');
        }
      }
    }
  }, [ sessionActive, remoteStream, isPeerVideoTrackMuted, peerVideoStatus ]);

  useEffect(() => {
    const videoElement = localVideoRef.current;
    if (!videoElement) return;

    if (!localStream) {
      console.log('Clearing local video (no stream)');
      videoElement.srcObject = null;
      return;
    }

    // Only attach if not already attached to this specific stream
    if (videoElement.srcObject !== localStream) {
      console.log('Attaching local stream (one time only)', localStream.id);
      videoElement.srcObject = localStream;
      videoElement.play().catch(err =>
        console.error('Error playing local video:', err)
      );
    }

    // No cleanup - let next stream replace this one
  }, [ localStream, swapPositions ]);

  useEffect(() => {
    const videoElement = remoteVideoRef.current;
    if (!videoElement) {
      console.error('No DOM element for remote video');
      return;
    }

    if (!remoteStream) {
      console.log('Clearing remote video (no stream)');
      videoElement.srcObject = null;
      setIsPeerVideoTrackMuted({ trackMuted: true });
      return;
    }

    // Only attach if not already attached to this specific stream
    if (videoElement.srcObject !== remoteStream) {
      console.log('Attaching remote stream (one time only)', remoteStream.id);
      videoElement.srcObject = remoteStream;
      videoElement.play().catch(err =>
        console.error('Error playing remote video:', err)
      );
    }

    // Set up track listeners for this stream
    const videoTracks = remoteStream.getVideoTracks();

    // Set initial muted state
    setIsPeerVideoTrackMuted({
      trackMuted: videoTracks.length === 0 || videoTracks.every(track => track.muted || !track.enabled)
    });

    // Set up track event listeners
    const trackListeners = videoTracks.map(track => {
      const onMute = () => {
        console.log('Remote video track muted');
        setIsPeerVideoTrackMuted({ trackMuted: true });
      };

      const onUnmute = () => {
        console.log('Remote video track unmuted');
        setIsPeerVideoTrackMuted({ trackMuted: false });
      };

      const onEnded = () => {
        console.log('Remote video track ended');
        setIsPeerVideoTrackMuted({ trackMuted: true });
      };

      track.addEventListener('mute', onMute);
      track.addEventListener('unmute', onUnmute);
      track.addEventListener('ended', onEnded);

      return { track, onMute, onUnmute, onEnded };
    });

    // Cleanup function
    return () => {
      trackListeners.forEach(({ track, onMute, onUnmute, onEnded }) => {
        track.removeEventListener('mute', onMute);
        track.removeEventListener('unmute', onUnmute);
        track.removeEventListener('ended', onEnded);
      });
    };
  }, [ remoteStream, swapPositions ]);

  // Shared props for both layouts
  const sharedLayoutProps = {
    localVideoRef,
    remoteVideoRef,
    canvasRef,
    localStream,
    remoteStream,
    isVideoEnabled,
    sessionActive,
    peerVideoStatus,
    rtcConnectionState,
    peerVideoEnabled,
    isPeerVideoHidden,
    peerAudioEnabled,
    selfFilter,
    peerFilter,
    swapPositions,
    isPeerAudioMuted,
    userCountry,
    peerCountry
  };

  // Declarative rendering based on layout type
  return currentLayout === 'default' ? (
    <DefaultLayout {...sharedLayoutProps} videoSizeRatio={videoSizeRatio} />
  ) : (
    <PiPLayout {...sharedLayoutProps} />
  );
});
